import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FirebaseInviteService, Invite } from '../services/firebase/FirebaseInviteService';
import EulaAgreement from '../components/client/EulaAgreement';
import WelcomeIntake from '../components/client/WelcomeIntake';
import SignatureModal from '../components/client/SignatureModal';
import AccountCreated from '../components/client/AccountCreated';
import ClientLogin from '../components/client/ClientLogin';
import ClientPortal from '../components/client/ClientPortal';
import TelehealthConsent from '../components/client/policies/TelehealthConsent';
import CreditCardAuthorization from '../components/client/policies/CreditCardAuthorization';
import InformedConsentPsychotherapy from '../components/client/policies/InformedConsentPsychotherapy';
import NoticeOfPrivacyPractices from '../components/client/policies/NoticeOfPrivacyPractices';
import PracticePolicies from '../components/client/policies/PracticePolicies';
import GAD7, { GAD7Answers } from '../components/client/questionnaires/GAD7';
import PHQ9, { PHQ9Answers } from '../components/client/questionnaires/PHQ9';
import StandardIntakeQuestionnaire, { StandardIntakeAnswers } from '../components/client/questionnaires/StandardIntakeQuestionnaire';
import { Signature } from '../types';
import { FirebasePatientService } from '../services/firebase/FirebasePatientService';
import { FirebaseFormResponseService } from '../services/firebase/FirebaseFormResponseService';
import { FirebaseFormAssignmentService } from '../services/firebase/FirebaseFormAssignmentService';
import firebaseAuthService from '../services/firebase/FirebaseAuthService';
import { useToast } from '../components/Toast';
import { AccessibilityService } from '../services/AccessibilityService';
import { HopeVoiceService } from '../services/HopeVoiceService';

const ClientRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshProfile } = useAuth();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(-1); // Start with -1 for EULA
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [invite, setInvite] = useState<Invite | null>(null);
  const inviteToken = searchParams.get('invite');

  // Resolve invite token on mount
  useEffect(() => {
    const resolveInvite = async () => {
      if (!inviteToken) return;
      try {
        const inv = await FirebaseInviteService.getInvite(inviteToken);
        if (inv && !FirebaseInviteService.isExpired(inv) && inv.status === 'pending') {
          setInvite(inv);
        }
      } catch (error) {
        console.error('Error resolving invite:', error);
      }
    };
    resolveInvite();
  }, [inviteToken]);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    preferredName: '',
    lastName: '',
    middleName: '',
    dateOfBirth: '',
    isMinor: false,
    gender: '',
    ssn: '',
    phone: '',
    phoneType: 'mobile',
    allowVoiceMessages: false,
    allowTextMessages: false,
    appointmentReminders: 'none',
    email: '',
    emailType: 'home',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    timeZone: '',
    relationshipStatus: '',
    employmentStatus: '',
    
    // Emergency Contact
    emergencyContactFirstName: '',
    emergencyContactLastName: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    emergencyContactRelation: '',
    allowEmailMessages: false,
    allowEmailAppointmentReminders: false,
    accessibilityReadAloud: false,
    accessibilityLargeText: false,
    accessibilityHighContrast: false,
    
    // Primary Insurance
    primaryInsuranceProvider: '',
    primaryMemberID: '',
    primaryGroupID: '',
    primaryPlanID: '',
    insurancePolicySex: '',
    insuranceStartDate: '',
    insuranceEndDate: '',
    clientRelationshipToInsured: '',
    insuranceCardFront: null as File | null,
    insuranceCardBack: null as File | null,
    primarySubscriberName: '',
    primarySubscriberDOB: '',
    primarySubscriberRelation: '',
    primaryInsurancePhone: '',
    
    // Secondary Insurance
    hasSecondaryInsurance: false,
    secondaryInsuranceProvider: '',
    secondaryPolicyNumber: '',
    secondaryGroupNumber: '',
    secondarySubscriberName: '',
    secondarySubscriberDOB: '',
    secondarySubscriberRelation: '',
    secondaryInsurancePhone: '',
    
    // Medical Information
    primaryPhysician: '',
    allergies: '',
    currentMedications: '',
    medicalHistory: '',

    // Pharmacy Information
    pharmacyName: '',
    pharmacyPhone: '',
    pharmacyFax: '',
    pharmacyAddress: '',
    pharmacyEmail: '',
    
    // Password Setup
    password: '',
    confirmPassword: '',

    // Consent and Agreements
    consentToTreatment: false,
    hipaaConsent: false,
    financialResponsibility: false,
    appointmentPolicy: false
  });

  const [signatureModal, setSignatureModal] = useState({
    isOpen: false,
    documentType: '',
    title: ''
  });
  const [signatures, setSignatures] = useState<Record<string, Signature>>({});
  const [accountCreated, setAccountCreated] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showTelehealthConsent, setShowTelehealthConsent] = useState(false);
  const [showCreditCardAuth, setShowCreditCardAuth] = useState(false);
  const [showInformedConsent, setShowInformedConsent] = useState(false);
  const [showPrivacyPractices, setShowPrivacyPractices] = useState(false);
  const [showPracticePolicies, setShowPracticePolicies] = useState(false);
  const [showGAD7, setShowGAD7] = useState(false);
  const [gad7Answers, setGAD7Answers] = useState<GAD7Answers | null>(null);
  const [showPHQ9, setShowPHQ9] = useState(false);
  const [phq9Answers, setPHQ9Answers] = useState<PHQ9Answers | null>(null);
  const [showStandardIntake, setShowStandardIntake] = useState(false);
  const [standardIntakeAnswers, setStandardIntakeAnswers] = useState<StandardIntakeAnswers | null>(null);

  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (stepErrors[name]) {
      setStepErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) errors.firstName = 'First name is required.';
      if (!formData.lastName.trim()) errors.lastName = 'Last name is required.';
      if (!formData.dateOfBirth) errors.dateOfBirth = 'Date of birth is required.';
      if (!formData.streetAddress.trim()) errors.streetAddress = 'Street address is required.';
      if (!formData.city.trim()) errors.city = 'City is required.';
      if (!formData.state) errors.state = 'State is required.';
      if (!formData.zipCode.trim()) errors.zipCode = 'Zip code is required.';
      if (!formData.timeZone) errors.timeZone = 'Time zone is required.';
    } else if (step === 2) {
      if (!formData.phone.trim()) errors.phone = 'Phone number is required.';
      if (!formData.email.trim()) errors.email = 'Email is required.';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Please enter a valid email address.';
    } else if (step === 3) {
      if (!formData.emergencyContactFirstName.trim()) errors.emergencyContactFirstName = 'Emergency contact first name is required.';
      if (!formData.emergencyContactLastName.trim()) errors.emergencyContactLastName = 'Emergency contact last name is required.';
      if (!formData.emergencyContactRelation) errors.emergencyContactRelation = 'Relationship is required.';
      if (!formData.emergencyContactPhone.trim()) errors.emergencyContactPhone = 'Emergency contact phone is required.';
    } else if (step === 4) {
      if (!formData.primaryInsuranceProvider) errors.primaryInsuranceProvider = 'Insurance provider is required.';
      if (!formData.primaryMemberID.trim()) errors.primaryMemberID = 'Member ID is required.';
      if (!formData.insurancePolicySex) errors.insurancePolicySex = 'Insurance policy sex is required.';
      if (!formData.clientRelationshipToInsured) errors.clientRelationshipToInsured = 'Relationship to insured is required.';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Patient self-registration is closed. Patients are provisioned by staff or
  // arrive through a staff-issued invite link
  // (FirebaseInviteService builds /client-register?invite=<id>), so the page
  // only opens with a token. The authoritative block is the Firestore rule
  // (users/{uid} create: false) — this is an honest screen, not the gate.
  if (!inviteToken) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A2540',
        padding: '2rem',
      }}>
        <div style={{
          maxWidth: '460px',
          textAlign: 'center',
          background: '#16325B',
          border: '1px solid #28527A',
          borderRadius: '12px',
          padding: '2.5rem',
          color: '#f9fafb',
        }}>
          <h1 style={{ fontSize: '1.4rem', margin: '0 0 0.75rem' }}>Registration is by invitation</h1>
          <p style={{ color: '#d1d5db', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
            Your provider's office creates your account and sends you a link to
            finish setting it up. If you're already registered, sign in below.
          </p>
          <button
            type="button"
            onClick={() => navigate('/client-login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#6CA6C1',
              fontSize: '0.95rem',
              cursor: 'pointer',
            }}
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  // WCAG helper: renders inline field error with role=alert
  const FieldError: React.FC<{ field: string }> = ({ field }) => {
    const err = stepErrors[field];
    if (!err) return null;
    return (
      <p id={`reg-${field}-error`} role="alert" aria-live="assertive" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
        {err}
      </p>
    );
  };

  // WCAG helper: returns aria props for a field
  const ariaFor = (field: string) => ({
    'aria-invalid': stepErrors[field] ? ('true' as const) : undefined,
    'aria-describedby': stepErrors[field] ? `reg-${field}-error` : undefined,
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.warning('Invalid File Type', 'Please upload only JPG, PNG, or PDF files.');
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.warning('File Too Large', 'File size must be less than 5MB.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const nextStep = () => {
    if (currentStep >= 1 && currentStep <= 4) {
      if (!validateStep(currentStep)) return;
    }
    if (currentStep < 6) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Get the current user (already created by Step 5 "Create Account" button)
      const { auth } = await import('../config/firebase');
      const currentUser = auth.currentUser;
      const patientId = currentUser?.uid || '';

      if (!patientId) {
        toast.warning('Account Required', 'Please create your account first (Step 5) before submitting documents.');
        return;
      }

      // Create patient record in Firestore using Auth UID as document ID
      // This ensures the client can read their own patient record via security rules
      await FirebasePatientService.createPatient({
        firstName: formData.firstName,
        lastName: formData.lastName || '',
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        address: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        insurance: {
          provider: formData.primaryInsuranceProvider || '',
          policyNumber: formData.primaryMemberID || '',
          groupNumber: formData.primaryGroupID || undefined,
        },
        emergencyContact: {
          name: `${formData.emergencyContactFirstName || ''} ${formData.emergencyContactLastName || ''}`.trim(),
          relationship: formData.emergencyContactRelation || '',
          phone: formData.emergencyContactPhone || '',
          email: formData.emergencyContactEmail || undefined,
        },
        allergies: formData.allergies,
        currentMedications: formData.currentMedications,
        medicalHistory: formData.medicalHistory,
        pharmacy: {
          name: formData.pharmacyName || '',
          phone: formData.pharmacyPhone || '',
          fax: formData.pharmacyFax || '',
          address: formData.pharmacyAddress || '',
          email: formData.pharmacyEmail || '',
        },
        assignedProviderId: invite?.providerId || '', // Set from invite if available
        status: 'active',
      }, patientId); // patientId = auth.currentUser.uid

      // If registered via invite, mark invite as accepted and auto-assign forms
      if (invite && inviteToken) {
        try {
          await FirebaseInviteService.acceptInvite(inviteToken, patientId);
        } catch (e) {
          // Non-blocking
        }

        // Auto-assign forms from the invite's selected documents
        if (invite.selectedDocuments && invite.selectedDocuments.length > 0) {
          try {
            // Forms completed during registration
            const completedIds: string[] = [];
            if (gad7Answers) completedIds.push('gad7');
            if (phq9Answers) completedIds.push('phq9');
            if (standardIntakeAnswers) completedIds.push('standard-intake');

            await FirebaseFormAssignmentService.assignFormsFromInvite(
              patientId,
              invite.providerId,
              invite.selectedDocuments,
              completedIds
            );
          } catch (e) {
            console.error('Error auto-assigning forms from invite:', e);
          }
        }
      }

      const resolvedProviderId = invite?.providerId || '';

      // Question text lookup maps — store human-readable text, not key names
      const gad7QuestionText: Record<string, string> = {
        question1: 'Feeling nervous, anxious, or on edge.',
        question2: 'Not being able to stop or control worrying.',
        question3: 'Worrying too much about different things.',
        question4: 'Trouble relaxing.',
        question5: "Being so restless that it's hard to sit still.",
        question6: 'Becoming easily annoyed or irritable.',
        question7: 'Feeling afraid as if something awful might happen.',
        difficultyLevel: 'How difficult have these problems made it for you?',
      };
      const phq9QuestionText: Record<string, string> = {
        question1: 'Little interest or pleasure in doing things.',
        question2: 'Feeling down, depressed, or hopeless.',
        question3: 'Trouble falling or staying asleep, or sleeping too much.',
        question4: 'Feeling tired or having little energy.',
        question5: 'Poor appetite or overeating.',
        question6: 'Feeling bad about yourself — or that you are a failure or have let yourself or your family down.',
        question7: 'Trouble concentrating on things, such as reading the newspaper or watching television.',
        question8: 'Moving or speaking so slowly that other people could have noticed. Or being so fidgety or restless that you have been moving around a lot more than usual.',
        question9: 'Thoughts that you would be better off dead or of hurting yourself in some way.',
        difficultyLevel: 'How difficult have these problems made it for you?',
      };
      const intakeQuestionText: Record<string, string> = {
        counselingReason: 'What brings you to counseling at this time?',
        counselingGoals: 'What are your goals for counseling?',
        previousMentalHealth: 'Have you seen a mental health professional before?',
        medications: 'Medications and supplements you are presently taking.',
        prescribingMD: 'Who is your prescribing MD?',
        primaryCarePhysician: 'Who is your primary care physician?',
        drinkAlcohol: 'Do you drink alcohol?',
        useRecreationalDrugs: 'Do you use recreational drugs?',
        suicidalThoughts: 'Do you have suicidal thoughts?',
        suicideAttempt: 'Have you ever attempted suicide?',
        harmOthersThoughts: 'Do you have thoughts or urges to harm others?',
        psychiatricHospitalization: 'Have you ever been hospitalized for a psychiatric issue?',
        familyMentalHealth: 'Is there a history of mental illness in your family?',
        relationshipStatus: 'Describe the nature of your relationship.',
        livingSituation: 'Describe your current living situation.',
        educationLevel: 'What is your level of education?',
        occupation: 'What is your current occupation?',
        pastSixMonthsSymptoms: 'Symptoms experienced in the past six months.',
        medicalConditions: 'Current medical conditions.',
        additionalInfo: 'What else would you like me to know?',
      };

      // Save questionnaire responses to Firestore
      const savePromises: Promise<string>[] = [];

      if (gad7Answers) {
        savePromises.push(
          FirebaseFormResponseService.saveResponse({
            assignmentId: `registration_gad7_${patientId}`,
            clientId: patientId,
            providerId: resolvedProviderId,
            formTemplateId: 'gad7',
            formTemplateName: 'GAD-7 (Generalized Anxiety Disorder)',
            responses: Object.entries(gad7Answers).map(([key, value]) => ({
              questionId: key,
              questionText: gad7QuestionText[key] || key,
              questionType: 'scale',
              answer: value,
            })),
            submittedBy: patientId,
          })
        );
      }

      if (phq9Answers) {
        savePromises.push(
          FirebaseFormResponseService.saveResponse({
            assignmentId: `registration_phq9_${patientId}`,
            clientId: patientId,
            providerId: resolvedProviderId,
            formTemplateId: 'phq9',
            formTemplateName: 'PHQ-9 (Patient Health Questionnaire)',
            responses: Object.entries(phq9Answers).map(([key, value]) => ({
              questionId: key,
              questionText: phq9QuestionText[key] || key,
              questionType: 'scale',
              answer: value,
            })),
            submittedBy: patientId,
          })
        );
      }

      if (standardIntakeAnswers) {
        savePromises.push(
          FirebaseFormResponseService.saveResponse({
            assignmentId: `registration_intake_${patientId}`,
            clientId: patientId,
            providerId: resolvedProviderId,
            formTemplateId: 'standard_intake',
            formTemplateName: 'Standard Intake Questionnaire',
            responses: Object.entries(standardIntakeAnswers).map(([key, value]) => ({
              questionId: key,
              questionText: intakeQuestionText[key] || key,
              questionType: 'text',
              answer: value,
            })),
            submittedBy: patientId,
          })
        );
      }

      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      // Apply accessibility preferences if selected during registration
      if (formData.accessibilityReadAloud) {
        HopeVoiceService.enable();
      }
      if (formData.accessibilityLargeText) {
        AccessibilityService.increaseFontSize(); // medium → large
        AccessibilityService.increaseFontSize(); // large → x-large
      }
      if (formData.accessibilityHighContrast) {
        AccessibilityService.toggleHighContrast();
      }

      // Refresh profile so AuthContext has correct client role
      await refreshProfile();
      // Go straight to client dashboard
      navigate('/client-dashboard');
    } catch (error: any) {
      console.error('Error completing registration:', error);
      toast.error('Registration Error', error.message || 'There was an error completing your registration. Please try again.');
    }
  };

  const openSignatureModal = (documentType: string, title: string) => {
    setSignatureModal({
      isOpen: true,
      documentType,
      title
    });
  };

  // Get the first available signature for reuse
  const getPreviousSignature = () => {
    const signatureKeys = Object.keys(signatures);
    if (signatureKeys.length > 0) {
      const firstSignature = signatures[signatureKeys[0]];
      return {
        signatureData: firstSignature.signatureData,
        signatureType: firstSignature.signatureType,
        typedName: firstSignature.typedName
      };
    }
    return undefined;
  };

  const closeSignatureModal = () => {
    setSignatureModal({
      isOpen: false,
      documentType: '',
      title: ''
    });
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    const signature: Signature = {
      id: `${signatureModal.documentType}_${Date.now()}`,
      documentId: signatureModal.documentType,
      clientId: 'temp_client_id', // This would come from actual client ID
      signatureData,
      signatureType,
      typedName,
      timestamp: new Date(),
      ipAddress: window.location.hostname,
      deviceInfo: navigator.userAgent
    };

    setSignatures(prev => ({
      ...prev,
      [signatureModal.documentType]: signature
    }));

    // Update form data to reflect signed status
    setFormData(prev => ({
      ...prev,
      [signatureModal.documentType]: true
    }));

    closeSignatureModal();
  };

  const handleLogin = (email: string, password: string) => {
    // Registration flow uses Firebase Auth directly via handleAccountCreation
    // This handles re-login with the credentials just created
    if (email === formData.email && formData.password) {
      setIsAuthenticated(true);
      setShowLogin(false);
    } else {
      // This would normally be handled by the login component
      console.error('Authentication failed');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowLogin(false);
    setAccountCreated(false);
    setEulaAccepted(false);
    setCurrentStep(-1); // Reset to EULA
  };

  const handleEulaAccept = (signature: string) => {
    setEulaAccepted(true);
    setCurrentStep(0); // Move to welcome screen
  };

  const handleTelehealthConsentAccept = (signature: string) => {
    // Create signature object for telehealth consent
    const telehealthSignature: Signature = {
      id: `telehealthConsent_${Date.now()}`,
      documentId: 'telehealthConsent',
      clientId: 'temp_client_id',
      signatureData: signature,
      signatureType: 'drawn',
      timestamp: new Date(),
      ipAddress: window.location.hostname,
      deviceInfo: navigator.userAgent
    };

    setSignatures(prev => ({
      ...prev,
      telehealthConsent: telehealthSignature
    }));

    // Update form data
    setFormData(prev => ({
      ...prev,
      consentToTreatment: true
    }));

    // Return to Documents view
    setShowTelehealthConsent(false);
  };

  const handleCreditCardAuthAccept = (signature: string) => {
    // Create signature object for credit card authorization
    const creditCardSignature: Signature = {
      id: `creditCardAuth_${Date.now()}`,
      documentId: 'creditCardAuth',
      clientId: 'temp_client_id',
      signatureData: signature,
      signatureType: 'drawn',
      timestamp: new Date(),
      ipAddress: window.location.hostname,
      deviceInfo: navigator.userAgent
    };

    setSignatures(prev => ({
      ...prev,
      creditCardAuth: creditCardSignature
    }));

    // Update form data
    setFormData(prev => ({
      ...prev,
      hipaaConsent: true
    }));

    // Return to Documents view
    setShowCreditCardAuth(false);
  };

  const handleInformedConsentAccept = (signature: string) => {
    // Create signature object for informed consent
    const informedConsentSignature: Signature = {
      id: `informedConsent_${Date.now()}`,
      documentId: 'informedConsent',
      clientId: 'temp_client_id',
      signatureData: signature,
      signatureType: 'drawn',
      timestamp: new Date(),
      ipAddress: window.location.hostname,
      deviceInfo: navigator.userAgent
    };

    setSignatures(prev => ({
      ...prev,
      informedConsent: informedConsentSignature
    }));

    // Update form data
    setFormData(prev => ({
      ...prev,
      financialResponsibility: true
    }));

    // Return to Documents view
    setShowInformedConsent(false);
  };

  const handlePrivacyPracticesAccept = (signature: string) => {
    // Create signature object for privacy practices
    const privacyPracticesSignature: Signature = {
      id: `privacyPractices_${Date.now()}`,
      documentId: 'privacyPractices',
      clientId: 'temp_client_id',
      signatureData: signature,
      signatureType: 'drawn',
      timestamp: new Date(),
      ipAddress: window.location.hostname,
      deviceInfo: navigator.userAgent
    };

    setSignatures(prev => ({
      ...prev,
      privacyPractices: privacyPracticesSignature
    }));

    // Update form data
    setFormData(prev => ({
      ...prev,
      appointmentPolicy: true
    }));

    // Return to Documents view
    setShowPrivacyPractices(false);
  };

  const handlePracticePoliciesAccept = (signature: string) => {
    // Create signature object for practice policies
    const practicePoliciesSignature: Signature = {
      id: `practicePolicies_${Date.now()}`,
      documentId: 'practicePolicies',
      clientId: 'temp_client_id',
      signatureData: signature,
      signatureType: 'drawn',
      timestamp: new Date(),
      ipAddress: window.location.hostname,
      deviceInfo: navigator.userAgent
    };

    setSignatures(prev => ({
      ...prev,
      practicePolicies: practicePoliciesSignature
    }));

    // Return to Documents view
    setShowPracticePolicies(false);
  };

  const handleGAD7Complete = (answers: GAD7Answers) => {
    // Store GAD-7 answers
    setGAD7Answers(answers);

    // Return to Documents view
    setShowGAD7(false);
  };

  const handlePHQ9Complete = (answers: PHQ9Answers) => {
    // Store PHQ-9 answers
    setPHQ9Answers(answers);

    // Return to Documents view
    setShowPHQ9(false);
  };

  const handleStandardIntakeComplete = (answers: StandardIntakeAnswers) => {
    // Store Standard Intake answers
    setStandardIntakeAnswers(answers);

    // Return to Documents view
    setShowStandardIntake(false);
  };

  const stepTitles = [
    'Personal Information',
    'Contact & Communication',
    'Emergency Contact',
    'Insurance Information',
    'Account Setup',
    'Documents'
  ];

  // Handle EULA agreement (must be first)
  if (currentStep === -1) {
    return (
      <EulaAgreement
        onAccept={handleEulaAccept}
      />
    );
  }

  // Handle welcome screen
  if (currentStep === 0) {
    return (
      <WelcomeIntake
        clientName={formData.firstName || "there"}
        onGetStarted={() => setCurrentStep(1)}
      />
    );
  }

  // Handle account created screen
  if (accountCreated) {
    return (
      <AccountCreated
        clientName={formData.firstName || "there"}
        email={formData.email}
        onContinue={() => {
          navigate('/client-login');
        }}
      />
    );
  }

  // Handle login screen
  if (showLogin) {
    return (
      <ClientLogin
        registrationEmail={formData.email}
        onLogin={handleLogin}
        onForgotPassword={async () => {
          if (!formData.email) {
            toast.warning('Email Required', 'Please enter your email address first.');
            return;
          }
          try {
            await firebaseAuthService.resetPassword(formData.email);
            toast.success('Reset Email Sent', `Password reset email sent to ${formData.email}. Please check your inbox.`);
          } catch (error: any) {
            toast.error('Reset Failed', error.message || 'Failed to send password reset email.');
          }
        }}
      />
    );
  }

  // Handle telehealth consent
  if (showTelehealthConsent) {
    return (
      <TelehealthConsent
        onAccept={handleTelehealthConsentAccept}
      />
    );
  }

  // Handle credit card authorization
  if (showCreditCardAuth) {
    return (
      <CreditCardAuthorization
        onAccept={handleCreditCardAuthAccept}
      />
    );
  }

  // Handle informed consent for psychotherapy
  if (showInformedConsent) {
    return (
      <InformedConsentPsychotherapy
        onAccept={handleInformedConsentAccept}
      />
    );
  }

  // Handle notice of privacy practices
  if (showPrivacyPractices) {
    return (
      <NoticeOfPrivacyPractices
        onAccept={handlePrivacyPracticesAccept}
      />
    );
  }

  // Handle practice policies
  if (showPracticePolicies) {
    return (
      <PracticePolicies
        onAccept={handlePracticePoliciesAccept}
      />
    );
  }

  // Handle GAD-7 questionnaire
  if (showGAD7) {
    return (
      <GAD7
        onComplete={handleGAD7Complete}
      />
    );
  }

  // Handle PHQ-9 questionnaire
  if (showPHQ9) {
    return (
      <PHQ9
        onComplete={handlePHQ9Complete}
      />
    );
  }

  // Handle Standard Intake Questionnaire
  if (showStandardIntake) {
    return (
      <StandardIntakeQuestionnaire
        onComplete={handleStandardIntakeComplete}
      />
    );
  }

  // Handle authenticated - redirect to client dashboard
  if (isAuthenticated) {
    window.location.href = '/client-dashboard';
    return null;
  }

  return (
    <div className="app">
      {/* WCAG 2.2 AA: Ensure checkbox/radio touch targets meet 44x44 minimum */}
      <style>{`
        .reg-form input[type="checkbox"],
        .reg-form input[type="radio"] {
          min-width: 20px;
          min-height: 20px;
          accent-color: #D4A017;
        }
        .reg-form label:has(input[type="checkbox"]),
        .reg-form label:has(input[type="radio"]) {
          min-height: 44px;
          display: flex;
          align-items: center;
        }
        .reg-form .field-error {
          color: #ef4444;
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }
      `}</style>
      <div className="reg-form" style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            My ClinicCore
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>Client Registration</p>
        </div>

        {/* Invite Banner */}
        {invite && (
          <div style={{
            background: 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)',
            borderRadius: '12px',
            padding: '1rem 1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid #10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>✉</span>
            <div>
              <p style={{ color: '#f9fafb', margin: 0, fontWeight: '600' }}>
                You've been invited by {invite.providerName}
              </p>
              <p style={{ color: '#a7f3d0', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                {invite.practiceName}
              </p>
            </div>
          </div>
        )}

        {/* Required Fields Legend */}
        <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
          Fields marked with <span aria-hidden="true">*</span><span className="sr-only">asterisk</span> are required.
        </p>

        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            {stepTitles.map((title, index) => (
              <div key={index} style={{
                flex: 1,
                textAlign: 'center',
                color: currentStep > index + 1 ? '#D4A017' : currentStep === index + 1 ? '#3b82f6' : '#6b7280',
                fontSize: '0.875rem',
                fontWeight: currentStep === index + 1 ? 'bold' : 'normal'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: currentStep > index + 1 ? '#D4A017' : currentStep === index + 1 ? '#3b82f6' : '#374151',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.5rem auto',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  {currentStep > index + 1 ? '✓' : index + 1}
                </div>
                {title}
              </div>
            ))}
          </div>
          <div style={{
            height: '4px',
            background: '#374151',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: '#D4A017',
              width: `${(currentStep / 6) * 100}%`,
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        <div>
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div>
              <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Personal Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="reg-firstName" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Legal First Name *
                  </label>
                  <input
                    id="reg-firstName"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('firstName')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.firstName ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="firstName" />
                </div>

                <div>
                  <label htmlFor="reg-lastName" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Last Name *
                  </label>
                  <input
                    id="reg-lastName"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('lastName')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.lastName ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="lastName" />
                </div>

                <div>
                  <label htmlFor="reg-preferredName" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    What name do you go by?
                  </label>
                  <input
                    id="reg-preferredName"
                    type="text"
                    name="preferredName"
                    value={formData.preferredName}
                    onChange={handleInputChange}
                    placeholder="Nickname or preferred name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="reg-dateOfBirth" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Date of Birth *
                  </label>
                  <input
                    id="reg-dateOfBirth"
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('dateOfBirth')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.dateOfBirth ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="dateOfBirth" />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f9fafb',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: '#374151',
                    borderRadius: '8px',
                    marginTop: '1.5rem'
                  }}>
                    <input
                      type="checkbox"
                      name="isMinor"
                      checked={formData.isMinor}
                      onChange={handleInputChange}
                      style={{ marginRight: '0.75rem', minWidth: '20px', minHeight: '20px', accentColor: '#D4A017' }}
                    />
                    Client is a Minor
                  </label>
                </div>


                <div>
                  <label htmlFor="reg-streetAddress" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Street Address *
                  </label>
                  <input
                    id="reg-streetAddress"
                    type="text"
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('streetAddress')}
                    placeholder="123 Main Street"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.streetAddress ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="streetAddress" />
                </div>

                <div>
                  <label htmlFor="reg-city" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    City *
                  </label>
                  <input
                    id="reg-city"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('city')}
                    placeholder="City"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.city ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="city" />
                </div>

                <div>
                  <label htmlFor="reg-state" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    State *
                  </label>
                  <select
                    id="reg-state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    {...ariaFor('state')}
                    aria-required="true"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                  <FieldError field="state" />
                </div>

                <div>
                  <label htmlFor="reg-zipCode" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Zip Code *
                  </label>
                  <input
                    id="reg-zipCode"
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('zipCode')}
                    placeholder="12345"
                    pattern="[0-9]{5}(-[0-9]{4})?"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.zipCode ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="zipCode" />
                </div>

                <div>
                  <label htmlFor="reg-timeZone" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Time Zone *
                  </label>
                  <select
                    id="reg-timeZone"
                    name="timeZone"
                    value={formData.timeZone}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('timeZone')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.timeZone ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Time Zone</option>
                    <option value="EST">Eastern Time (EST)</option>
                    <option value="CST">Central Time (CST)</option>
                    <option value="MST">Mountain Time (MST)</option>
                    <option value="PST">Pacific Time (PST)</option>
                    <option value="AKST">Alaska Time (AKST)</option>
                    <option value="HST">Hawaii Time (HST)</option>
                  </select>
                  <FieldError field="timeZone" />
                </div>

                <div>
                  <label htmlFor="reg-relationshipStatus" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Relationship Status
                  </label>
                  <select
                    id="reg-relationshipStatus"
                    name="relationshipStatus"
                    value={formData.relationshipStatus}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Status</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                    <option value="separated">Separated</option>
                    <option value="domestic_partnership">Domestic Partnership</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="reg-employmentStatus" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Employment Status
                  </label>
                  <select
                    id="reg-employmentStatus"
                    name="employmentStatus"
                    value={formData.employmentStatus}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Status</option>
                    <option value="employed_full_time">Employed - Full Time</option>
                    <option value="employed_part_time">Employed - Part Time</option>
                    <option value="self_employed">Self-Employed</option>
                    <option value="unemployed">Unemployed</option>
                    <option value="student">Student</option>
                    <option value="retired">Retired</option>
                    <option value="disabled">Disabled</option>
                    <option value="homemaker">Homemaker</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

            </div>
          )}

          {/* Step 2: Contact & Communication */}
          {currentStep === 2 && (
            <div>
              <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Contact & Communication</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="reg-phone" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Phone Number *
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      id="reg-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      {...ariaFor('phone')}
                      placeholder="(555) 123-4567"
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: stepErrors.phone ? '2px solid #ef4444' : '1px solid #374151',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                    <select
                      name="phoneType"
                      value={formData.phoneType}
                      onChange={handleInputChange}
                      aria-label="Phone type"
                      style={{
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #374151',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem',
                        minWidth: '100px'
                      }}
                    >
                      <option value="mobile">Mobile</option>
                      <option value="home">Home</option>
                      <option value="work">Work</option>
                    </select>
                  </div>
                  <FieldError field="phone" />
                </div>

                <div>
                  <label htmlFor="reg-email" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Email *
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      id="reg-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      {...ariaFor('email')}
                      autoComplete="email"
                      placeholder="your@email.com"
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: stepErrors.email ? '2px solid #ef4444' : '1px solid #374151',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                    <select
                      name="emailType"
                      value={formData.emailType}
                      onChange={handleInputChange}
                      aria-label="Email type"
                      style={{
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid #374151',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem',
                        minWidth: '100px'
                      }}
                    >
                      <option value="home">Home</option>
                      <option value="work">Work</option>
                    </select>
                  </div>
                  <FieldError field="email" />
                </div>

              </div>

              {/* Phone Communication Preferences */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ color: '#f9fafb', marginBottom: '1rem', fontSize: '1.1rem' }}>
                  Phone Communication Preferences
                </h3>

                {/* Voice and Text Message Checkboxes */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f9fafb',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: '#374151',
                    borderRadius: '8px'
                  }}>
                    <input
                      type="checkbox"
                      name="allowVoiceMessages"
                      checked={formData.allowVoiceMessages}
                      onChange={handleInputChange}
                      style={{ marginRight: '0.75rem' }}
                    />
                    Please send voice messages to this number
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f9fafb',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: '#374151',
                    borderRadius: '8px'
                  }}>
                    <input
                      type="checkbox"
                      name="allowTextMessages"
                      checked={formData.allowTextMessages}
                      onChange={handleInputChange}
                      style={{ marginRight: '0.75rem' }}
                    />
                    Please send text messages to this number
                  </label>
                </div>

                {/* Appointment Reminder Options */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#f9fafb', marginBottom: '0.75rem', fontSize: '1rem' }}>
                    Appointment Reminders
                  </h4>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.appointmentReminders === 'none' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.appointmentReminders === 'none' ? '2px solid #3b82f6' : '1px solid #4b5563'
                    }}>
                      <input
                        type="radio"
                        name="appointmentReminders"
                        value="none"
                        checked={formData.appointmentReminders === 'none'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      No, do not send appointment reminders to this number
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.appointmentReminders === 'text' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.appointmentReminders === 'text' ? '2px solid #3b82f6' : '1px solid #4b5563'
                    }}>
                      <input
                        type="radio"
                        name="appointmentReminders"
                        value="text"
                        checked={formData.appointmentReminders === 'text'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Yes, send text appointment reminders to this number
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.appointmentReminders === 'voice' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.appointmentReminders === 'voice' ? '2px solid #3b82f6' : '1px solid #4b5563'
                    }}>
                      <input
                        type="radio"
                        name="appointmentReminders"
                        value="voice"
                        checked={formData.appointmentReminders === 'voice'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Yes, send voice appointment reminders to this number
                    </label>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Step 3: Emergency Contact */}
          {currentStep === 3 && (
            <div>
              <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Emergency Contact Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="reg-emergencyContactFirstName" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Emergency Contact First Name *
                  </label>
                  <input
                    id="reg-emergencyContactFirstName"
                    type="text"
                    name="emergencyContactFirstName"
                    value={formData.emergencyContactFirstName}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('emergencyContactFirstName')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.emergencyContactFirstName ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="emergencyContactFirstName" />
                </div>

                <div>
                  <label htmlFor="reg-emergencyContactLastName" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Emergency Contact Last Name *
                  </label>
                  <input
                    id="reg-emergencyContactLastName"
                    type="text"
                    name="emergencyContactLastName"
                    value={formData.emergencyContactLastName}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('emergencyContactLastName')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.emergencyContactLastName ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="emergencyContactLastName" />
                </div>

                <div>
                  <label htmlFor="reg-emergencyContactRelation" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Emergency Contact Relationship *
                  </label>
                  <select
                    id="reg-emergencyContactRelation"
                    name="emergencyContactRelation"
                    value={formData.emergencyContactRelation}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('emergencyContactRelation')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.emergencyContactRelation ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Relationship</option>
                    <option value="spouse">Spouse</option>
                    <option value="parent">Parent</option>
                    <option value="child">Child</option>
                    <option value="sibling">Sibling</option>
                    <option value="grandparent">Grandparent</option>
                    <option value="grandchild">Grandchild</option>
                    <option value="aunt_uncle">Aunt/Uncle</option>
                    <option value="cousin">Cousin</option>
                    <option value="friend">Friend</option>
                    <option value="partner">Partner</option>
                    <option value="guardian">Guardian</option>
                    <option value="caregiver">Caregiver</option>
                    <option value="other">Other</option>
                  </select>
                  <FieldError field="emergencyContactRelation" />
                </div>

                <div>
                  <label htmlFor="reg-emergencyContactPhone" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Emergency Contact Phone *
                  </label>
                  <input
                    id="reg-emergencyContactPhone"
                    type="tel"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('emergencyContactPhone')}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.emergencyContactPhone ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="emergencyContactPhone" />
                </div>
              </div>

              {/* Phone Communication Preferences */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ color: '#f9fafb', marginBottom: '1rem', fontSize: '1.1rem' }}>
                  Phone Communication Preferences
                </h3>

                {/* Voice and Text Message Checkboxes */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f9fafb',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: '#374151',
                    borderRadius: '8px'
                  }}>
                    <input
                      type="checkbox"
                      name="allowVoiceMessages"
                      checked={formData.allowVoiceMessages}
                      onChange={handleInputChange}
                      style={{ marginRight: '0.75rem' }}
                    />
                    Please send voice messages to this number
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#f9fafb',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: '#374151',
                    borderRadius: '8px'
                  }}>
                    <input
                      type="checkbox"
                      name="allowTextMessages"
                      checked={formData.allowTextMessages}
                      onChange={handleInputChange}
                      style={{ marginRight: '0.75rem' }}
                    />
                    Please send text messages to this number
                  </label>
                </div>

                {/* Appointment Reminder Options */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#f9fafb', marginBottom: '0.75rem', fontSize: '1rem' }}>
                    Appointment Reminders
                  </h4>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.appointmentReminders === 'none' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.appointmentReminders === 'none' ? '2px solid #3b82f6' : '1px solid #4b5563'
                    }}>
                      <input
                        type="radio"
                        name="appointmentReminders"
                        value="none"
                        checked={formData.appointmentReminders === 'none'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      No, do not send appointment reminders to this number
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.appointmentReminders === 'text' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.appointmentReminders === 'text' ? '2px solid #3b82f6' : '1px solid #4b5563'
                    }}>
                      <input
                        type="radio"
                        name="appointmentReminders"
                        value="text"
                        checked={formData.appointmentReminders === 'text'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Yes, send text appointment reminders to this number
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.appointmentReminders === 'voice' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.appointmentReminders === 'voice' ? '2px solid #3b82f6' : '1px solid #4b5563'
                    }}>
                      <input
                        type="radio"
                        name="appointmentReminders"
                        value="voice"
                        checked={formData.appointmentReminders === 'voice'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Yes, send voice appointment reminders to this number
                    </label>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Email */}
              <div style={{ marginTop: '2rem' }}>
                <div>
                  <label htmlFor="reg-emergencyContactEmail" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Emergency Contact Email
                  </label>
                  <input
                    id="reg-emergencyContactEmail"
                    type="email"
                    name="emergencyContactEmail"
                    value={formData.emergencyContactEmail}
                    onChange={handleInputChange}
                    placeholder="emergency@email.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem',
                      maxWidth: '400px'
                    }}
                  />
                </div>

                {/* Email Communication Preferences */}
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#f9fafb', marginBottom: '0.75rem', fontSize: '1rem' }}>
                    Email Communication Preferences
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: '#374151',
                      borderRadius: '8px'
                    }}>
                      <input
                        type="checkbox"
                        name="allowEmailMessages"
                        checked={formData.allowEmailMessages}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Please send messages to this email
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: '#374151',
                      borderRadius: '8px'
                    }}>
                      <input
                        type="checkbox"
                        name="allowEmailAppointmentReminders"
                        checked={formData.allowEmailAppointmentReminders}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Please send appointment reminders to this email
                    </label>
                  </div>
                </div>

                {/* Accessibility Preferences */}
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ color: '#f9fafb', marginBottom: '0.5rem', fontSize: '1rem' }}>
                    Accessibility Preferences
                  </h4>
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    Do you need help reading or navigating the app? Select any that apply.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: '#374151',
                      borderRadius: '8px'
                    }}>
                      <input
                        type="checkbox"
                        name="accessibilityReadAloud"
                        checked={formData.accessibilityReadAloud || false}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Read Aloud — Hope reads the screen for me
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: '#374151',
                      borderRadius: '8px'
                    }}>
                      <input
                        type="checkbox"
                        name="accessibilityLargeText"
                        checked={formData.accessibilityLargeText || false}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      Large Text — make everything bigger
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: '#374151',
                      borderRadius: '8px'
                    }}>
                      <input
                        type="checkbox"
                        name="accessibilityHighContrast"
                        checked={formData.accessibilityHighContrast || false}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.75rem' }}
                      />
                      High Contrast — bold colors for easier reading
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Insurance Information */}
          {currentStep === 4 && (
            <div>
              <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Insurance Information</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="reg-primaryInsuranceProvider" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Insurance Provider *
                  </label>
                  <select
                    id="reg-primaryInsuranceProvider"
                    name="primaryInsuranceProvider"
                    value={formData.primaryInsuranceProvider}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('primaryInsuranceProvider')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.primaryInsuranceProvider ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Insurance Provider</option>
                    <option value="aetna">Aetna</option>
                    <option value="anthem">Anthem</option>
                    <option value="blue_cross_blue_shield">Blue Cross Blue Shield</option>
                    <option value="cigna">Cigna</option>
                    <option value="humana">Humana</option>
                    <option value="kaiser_permanente">Kaiser Permanente</option>
                    <option value="molina_healthcare">Molina Healthcare</option>
                    <option value="unitedhealth">UnitedHealth</option>
                    <option value="wellcare">WellCare</option>
                    <option value="medicaid">Medicaid</option>
                    <option value="medicare">Medicare</option>
                    <option value="tricare">TRICARE</option>
                    <option value="other">Other</option>
                  </select>
                  <FieldError field="primaryInsuranceProvider" />
                </div>

                <div>
                  <label htmlFor="reg-primaryMemberID" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Member ID *
                  </label>
                  <input
                    id="reg-primaryMemberID"
                    type="text"
                    name="primaryMemberID"
                    value={formData.primaryMemberID}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('primaryMemberID')}
                    placeholder="Member ID number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.primaryMemberID ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <FieldError field="primaryMemberID" />
                </div>

                <div>
                  <label htmlFor="reg-primaryGroupID" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Group ID
                  </label>
                  <input
                    id="reg-primaryGroupID"
                    type="text"
                    name="primaryGroupID"
                    value={formData.primaryGroupID}
                    onChange={handleInputChange}
                    placeholder="Group ID (if applicable)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="reg-primaryPlanID" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Plan ID
                  </label>
                  <input
                    id="reg-primaryPlanID"
                    type="text"
                    name="primaryPlanID"
                    value={formData.primaryPlanID}
                    onChange={handleInputChange}
                    placeholder="Plan ID (if applicable)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    What sex is on your insurance policy? *
                  </label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.insurancePolicySex === 'female' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.insurancePolicySex === 'female' ? '2px solid #3b82f6' : '1px solid #4b5563',
                      flex: 1,
                      justifyContent: 'center'
                    }}>
                      <input
                        type="radio"
                        name="insurancePolicySex"
                        value="female"
                        checked={formData.insurancePolicySex === 'female'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.5rem' }}
                        required
                        aria-label="Insurance policy sex selection"
                      />
                      Female
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#f9fafb',
                      cursor: 'pointer',
                      padding: '0.75rem',
                      background: formData.insurancePolicySex === 'male' ? '#1f2937' : '#374151',
                      borderRadius: '8px',
                      border: formData.insurancePolicySex === 'male' ? '2px solid #3b82f6' : '1px solid #4b5563',
                      flex: 1,
                      justifyContent: 'center'
                    }}>
                      <input
                        type="radio"
                        name="insurancePolicySex"
                        value="male"
                        checked={formData.insurancePolicySex === 'male'}
                        onChange={handleInputChange}
                        style={{ marginRight: '0.5rem' }}
                        required
                        aria-label="Insurance policy sex selection"
                      />
                      Male
                    </label>
                  </div>
                  <FieldError field="insurancePolicySex" />
                </div>

                <div>
                  <label htmlFor="reg-insuranceStartDate" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Insurance Start Date
                  </label>
                  <input
                    id="reg-insuranceStartDate"
                    type="date"
                    name="insuranceStartDate"
                    value={formData.insuranceStartDate}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="reg-insuranceEndDate" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Insurance End Date
                  </label>
                  <input
                    id="reg-insuranceEndDate"
                    type="date"
                    name="insuranceEndDate"
                    value={formData.insuranceEndDate}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="reg-clientRelationshipToInsured" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem' }}>
                    Client's relationship to insured *
                  </label>
                  <select
                    id="reg-clientRelationshipToInsured"
                    name="clientRelationshipToInsured"
                    value={formData.clientRelationshipToInsured}
                    onChange={handleInputChange}
                    required
                    aria-required="true"
                    {...ariaFor('clientRelationshipToInsured')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: stepErrors.clientRelationshipToInsured ? '2px solid #ef4444' : '1px solid #374151',
                      background: '#374151',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Relationship</option>
                    <option value="client">Client</option>
                    <option value="client_spouse">Client's Spouse</option>
                    <option value="client_parent">Client's Parent</option>
                    <option value="other">Other</option>
                  </select>
                  <FieldError field="clientRelationshipToInsured" />
                </div>
              </div>

              {/* Insurance Card Upload */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ color: '#f9fafb', marginBottom: '1rem', fontSize: '1.1rem' }}>
                  Insurance Card
                </h3>
                <p style={{ color: '#d1d5db', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                  Upload an image of the front and back of your insurance card to keep on file.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                  {/* Front of Insurance Card */}
                  <div style={{
                    padding: '2rem',
                    border: '2px dashed #6b7280',
                    borderRadius: '8px',
                    textAlign: 'center',
                    background: '#374151',
                    position: 'relative',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      aria-label="Upload front of insurance card (JPG, PNG, or PDF)"
                      onChange={(e) => handleFileUpload(e, 'insuranceCardFront')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ pointerEvents: 'none' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: '#6b7280',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem auto',
                        fontSize: '1.5rem',
                        color: '#f9fafb'
                      }}>
                        ⬆
                      </div>
                      <h4 style={{ color: '#f9fafb', margin: '0 0 0.5rem 0', fontSize: '1rem' }}>
                        Front of Insurance Card
                      </h4>
                      <p style={{
                        color: formData.insuranceCardFront ? '#10b981' : '#6CA6C1',
                        margin: '0 0 0.25rem 0',
                        fontSize: '0.875rem',
                        fontWeight: formData.insuranceCardFront ? 'bold' : 'normal'
                      }}>
                        {/* Force recompilation */ formData.insuranceCardFront ? (formData.insuranceCardFront as File).name : 'Choose file or drag and drop file'}
                      </p>
                      <p style={{ color: '#d1d5db', margin: 0, fontSize: '0.75rem' }}>
                        Only JPG, PNG, & PDF files
                      </p>
                    </div>
                  </div>

                  {/* Back of Insurance Card */}
                  <div style={{
                    padding: '2rem',
                    border: '2px dashed #6b7280',
                    borderRadius: '8px',
                    textAlign: 'center',
                    background: '#374151',
                    position: 'relative',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      aria-label="Upload back of insurance card (JPG, PNG, or PDF)"
                      onChange={(e) => handleFileUpload(e, 'insuranceCardBack')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ pointerEvents: 'none' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: '#6b7280',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem auto',
                        fontSize: '1.5rem',
                        color: '#f9fafb'
                      }}>
                        ⬆
                      </div>
                      <h4 style={{ color: '#f9fafb', margin: '0 0 0.5rem 0', fontSize: '1rem' }}>
                        Back of Insurance Card
                      </h4>
                      <p style={{
                        color: formData.insuranceCardBack ? '#10b981' : '#6CA6C1',
                        margin: '0 0 0.25rem 0',
                        fontSize: '0.875rem',
                        fontWeight: formData.insuranceCardBack ? 'bold' : 'normal'
                      }}>
                        {/* Force recompilation */ formData.insuranceCardBack ? (formData.insuranceCardBack as File).name : 'Choose file or drag and drop file'}
                      </p>
                      <p style={{ color: '#d1d5db', margin: 0, fontSize: '0.75rem' }}>
                        Only JPG, PNG, & PDF files
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pharmacy Information */}
                <div style={{
                  background: '#113F67',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #6CA6C1',
                  marginTop: '2rem'
                }}>
                  <h3 style={{ color: '#F7E9A0', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                    Preferred Pharmacy
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: '0.85rem', marginBottom: '1rem' }}>
                    Your pharmacy information tells your provider where to send your prescriptions.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', color: '#f9fafb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                        Pharmacy Name
                      </label>
                      <input
                        name="pharmacyName"
                        value={formData.pharmacyName}
                        onChange={handleInputChange}
                        placeholder="e.g., CVS Pharmacy, Walgreens, Rite Aid"
                        style={{
                          width: '100%', padding: '0.75rem', borderRadius: '8px',
                          border: '1px solid #374151', background: '#374151',
                          color: '#f9fafb', fontSize: '1rem', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#f9fafb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                        Pharmacy Phone
                      </label>
                      <input
                        name="pharmacyPhone"
                        value={formData.pharmacyPhone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        style={{
                          width: '100%', padding: '0.75rem', borderRadius: '8px',
                          border: '1px solid #374151', background: '#374151',
                          color: '#f9fafb', fontSize: '1rem', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#f9fafb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                        Pharmacy Fax
                      </label>
                      <input
                        name="pharmacyFax"
                        value={formData.pharmacyFax}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4568"
                        style={{
                          width: '100%', padding: '0.75rem', borderRadius: '8px',
                          border: '1px solid #374151', background: '#374151',
                          color: '#f9fafb', fontSize: '1rem', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', color: '#f9fafb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                        Pharmacy Address
                      </label>
                      <input
                        name="pharmacyAddress"
                        value={formData.pharmacyAddress}
                        onChange={handleInputChange}
                        placeholder="123 Main St, City, State ZIP"
                        style={{
                          width: '100%', padding: '0.75rem', borderRadius: '8px',
                          border: '1px solid #374151', background: '#374151',
                          color: '#f9fafb', fontSize: '1rem', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', color: '#f9fafb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                        Pharmacy Email
                      </label>
                      <input
                        name="pharmacyEmail"
                        value={formData.pharmacyEmail}
                        onChange={handleInputChange}
                        placeholder="pharmacy@example.com"
                        style={{
                          width: '100%', padding: '0.75rem', borderRadius: '8px',
                          border: '1px solid #374151', background: '#374151',
                          color: '#f9fafb', fontSize: '1rem', boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button for Insurance Information */}
                <div style={{
                  textAlign: 'center',
                  marginTop: '2rem',
                  paddingTop: '2rem',
                  borderTop: '1px solid #374151'
                }}>
                  <button
                    onClick={() => { if (validateStep(4)) setCurrentStep(5); }}
                    style={{
                      background: 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)',
                      color: 'white',
                      padding: '1rem 3rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      boxShadow: '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(212, 160, 23, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(212, 160, 23, 0.3)';
                    }}
                  >
                    Continue to Account Setup
                  </button>
                  <p style={{
                    color: '#d1d5db',
                    fontSize: '0.875rem',
                    marginTop: '1rem',
                    lineHeight: '1.5'
                  }}>
                    You'll create a secure password to access your medical forms and schedule appointments
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Password Setup */}
          {currentStep === 5 && (
            <div>
              <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Create Your Account</h2>
              <div style={{
                background: '#113F67',
                borderRadius: '12px',
                padding: '2rem',
                border: '1px solid #6CA6C1',
                marginBottom: '1.5rem'
              }}>
                <p style={{
                  color: '#d1d5db',
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  marginBottom: '1.5rem'
                }}>
                  Create a secure password to access your medical forms and schedule appointments.
                  You'll use your email address <strong style={{ color: '#f9fafb' }}>({formData.email})</strong> to log in.
                </p>

                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Password Field */}
                  <div>
                    <label htmlFor="reg-password" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="reg-password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        aria-label="Insurance policy sex selection"
                        aria-describedby="password-requirements"
                        autoComplete="new-password"
                        placeholder="Enter a secure password"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          paddingRight: '3.5rem',
                          borderRadius: '8px',
                          border: '1px solid #374151',
                          background: '#374151',
                          color: '#f9fafb',
                          fontSize: '1rem'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#d1d5db',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          minWidth: '44px',
                          minHeight: '44px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p id="password-requirements" style={{ color: '#d1d5db', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      Must be at least 8 characters with letters and numbers
                    </p>
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label htmlFor="reg-confirmPassword" style={{ display: 'block', color: '#f9fafb', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Confirm Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                    <input
                      id="reg-confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      aria-describedby={formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword ? 'password-mismatch-error' : undefined}
                      aria-invalid={formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword ? 'true' : undefined}
                      autoComplete="new-password"
                      placeholder="Confirm your password"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '3.5rem',
                        borderRadius: '8px',
                        border: formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword
                          ? '2px solid #ef4444'
                          : '1px solid #374151',
                        background: '#374151',
                        color: '#f9fafb',
                        fontSize: '1rem'
                      }}
                    />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#d1d5db',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          minWidth: '44px',
                          minHeight: '44px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p id="password-mismatch-error" role="alert" aria-live="assertive" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        Passwords do not match. Please re-enter your password.
                      </p>
                    )}
                  </div>
                </div>

                {/* Create Account Button */}
                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                  <button
                    onClick={async () => {
                      try {
                        // Create Firebase Auth account
                        const displayName = `${formData.firstName} ${formData.lastName}`.trim();
                        await firebaseAuthService.registerClient(
                          formData.email,
                          formData.password,
                          displayName
                        );
                        // Now create the patient record + save questionnaires via handleSubmit
                        // handleSubmit will call refreshProfile + navigate to dashboard
                        await handleSubmit({ preventDefault: () => {} } as React.FormEvent);
                      } catch (error: any) {
                        toast.error('Account Creation Failed', error.message || 'Failed to create account. Please try again.');
                      }
                    }}
                    disabled={!formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword}
                    style={{
                      background: formData.password && formData.confirmPassword && formData.password === formData.confirmPassword
                        ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                        : '#6b7280',
                      color: 'white',
                      padding: '1rem 3rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: formData.password && formData.confirmPassword && formData.password === formData.confirmPassword ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      boxShadow: formData.password && formData.confirmPassword && formData.password === formData.confirmPassword
                        ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                        : 'none'
                    }}
                  >
                    Create Account
                  </button>
                  <p style={{
                    color: '#d1d5db',
                    fontSize: '0.875rem',
                    marginTop: '1rem',
                    lineHeight: '1.5'
                  }}>
                    After creating your account, you'll complete medical forms and consent documents
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Documents */}
          {currentStep === 6 && (
            <div>
              <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>Documents</h2>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {/* Consent for Telehealth Consultation */}
                <div style={{
                  padding: '1rem',
                  background: signatures.telehealthConsent ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: signatures.telehealthConsent ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      Consent for Telehealth Consultation
                    </h3>
                    {signatures.telehealthConsent && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Signed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    I consent to telehealth consultation and understand the technology, risks and benefits involved.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowTelehealthConsent(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: signatures.telehealthConsent ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {signatures.telehealthConsent ? 'Update Signature' : 'Sign Document'}
                  </button>
                </div>

                {/* Credit Card Authorization */}
                <div style={{
                  padding: '1rem',
                  background: signatures.creditCardAuth ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: signatures.creditCardAuth ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      Credit Card Authorization
                    </h3>
                    {signatures.creditCardAuth && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Signed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    I authorize charges to my credit card through Stripe for services rendered.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCreditCardAuth(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: signatures.creditCardAuth ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {signatures.creditCardAuth ? 'Update Signature' : 'Sign Document'}
                  </button>
                </div>

                {/* Informed Consent for Psychotherapy */}
                <div style={{
                  padding: '1rem',
                  background: signatures.informedConsent ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: signatures.informedConsent ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      Informed Consent for Psychotherapy
                    </h3>
                    {signatures.informedConsent && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Signed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    I understand the therapeutic process, confidentiality, and consent to psychotherapy treatment.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowInformedConsent(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: signatures.informedConsent ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {signatures.informedConsent ? 'Update Signature' : 'Sign Document'}
                  </button>
                </div>

                {/* Notice of Privacy Practices */}
                <div style={{
                  padding: '1rem',
                  background: signatures.privacyPractices ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: signatures.privacyPractices ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      Notice of Privacy Practices
                    </h3>
                    {signatures.privacyPractices && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Signed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    I acknowledge receipt and review of the HIPAA Notice of Privacy Practices.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPrivacyPractices(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: signatures.privacyPractices ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {signatures.privacyPractices ? 'Update Signature' : 'Sign Document'}
                  </button>
                </div>

                {/* Practice Policies */}
                <div style={{
                  padding: '1rem',
                  background: signatures.practicePolicies ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: signatures.practicePolicies ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      Practice Policies
                    </h3>
                    {signatures.practicePolicies && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Signed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    I have read and agree to abide by the practice policies for appointments, communication, and treatment.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPracticePolicies(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: signatures.practicePolicies ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {signatures.practicePolicies ? 'Update Signature' : 'Sign Document'}
                  </button>
                </div>

                {/* GAD-7 Questionnaire */}
                <div style={{
                  padding: '1rem',
                  background: gad7Answers ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: gad7Answers ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      GAD-7 Anxiety Scale
                    </h3>
                    {gad7Answers && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Completed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    Complete the GAD-7 anxiety assessment questionnaire (8 questions).
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowGAD7(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: gad7Answers ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {gad7Answers ? 'Update Responses' : 'Complete Assessment'}
                  </button>
                </div>

                {/* PHQ-9 Questionnaire */}
                <div style={{
                  padding: '1rem',
                  background: phq9Answers ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: phq9Answers ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      PHQ-9 Depression Scale
                    </h3>
                    {phq9Answers && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Completed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    Complete the PHQ-9 depression assessment questionnaire (10 questions).
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPHQ9(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: phq9Answers ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {phq9Answers ? 'Update Responses' : 'Complete Assessment'}
                  </button>
                </div>

                {/* Standard Intake Questionnaire */}
                <div style={{
                  padding: '1rem',
                  background: standardIntakeAnswers ? '#065f46' : '#374151',
                  borderRadius: '8px',
                  border: standardIntakeAnswers ? '2px solid #10b981' : '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{ color: '#f9fafb', margin: 0, fontSize: '1rem' }}>
                      Standard Intake Questionnaire
                    </h3>
                    {standardIntakeAnswers && (
                      <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold' }}>
                        ✓ Completed
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    Complete the comprehensive intake questionnaire with personal and medical history.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowStandardIntake(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: standardIntakeAnswers ? '#6b7280' : '#3b82f6',
                      color: '#f9fafb',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {standardIntakeAnswers ? 'Update Responses' : 'Complete Questionnaire'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginTop: '2rem',
            paddingTop: '2rem',
            borderTop: '1px solid #374151'
          }}>
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                background: currentStep === 1 ? '#6b7280' : '#374151',
                color: '#f9fafb',
                fontSize: '1rem',
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 1 ? 0.5 : 1
              }}
            >
              Previous
            </button>

            <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
              Step {currentStep} of 5
            </div>

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={nextStep}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#3b82f6',
                  color: '#f9fafb',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Next
              </button>
            ) : currentStep === 5 ? (
              <button
                type="button"
                onClick={handleSubmit}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#D4A017',
                  color: '#f9fafb',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Create Account
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!signatures.telehealthConsent || !signatures.creditCardAuth || !signatures.informedConsent || !signatures.privacyPractices || !signatures.practicePolicies || !gad7Answers || !phq9Answers || !standardIntakeAnswers}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: (signatures.telehealthConsent && signatures.creditCardAuth && signatures.informedConsent && signatures.privacyPractices && signatures.practicePolicies && gad7Answers && phq9Answers && standardIntakeAnswers) ? '#D4A017' : '#6b7280',
                  color: '#f9fafb',
                  fontSize: '1rem',
                  cursor: (signatures.telehealthConsent && signatures.creditCardAuth && signatures.informedConsent && signatures.privacyPractices && signatures.practicePolicies && gad7Answers && phq9Answers && standardIntakeAnswers) ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                  opacity: (signatures.telehealthConsent && signatures.creditCardAuth && signatures.informedConsent && signatures.privacyPractices && signatures.practicePolicies && gad7Answers && phq9Answers && standardIntakeAnswers) ? 1 : 0.6
                }}
              >
                Complete Registration
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      <SignatureModal
        isOpen={signatureModal.isOpen}
        onClose={closeSignatureModal}
        onSignatureComplete={handleSignatureComplete}
        title={signatureModal.title}
        clientName={`${formData.firstName} ${formData.lastName}`.trim() || 'Client'}
        previousSignature={getPreviousSignature()}
      />
    </div>
  );
};

export default ClientRegistration;